<template>
    <div>
        <!-- Loader Component -->
        <Loader :visible="isLoading" :text="loadingText" />

        <!-- Confirmation Modal for Issue Orders -->
        <ConfirmationModal :show="showIssueConfirmation" @close="showIssueConfirmation = false">
            <template #title>Conferma Emissione Ordine</template>

            <template #content>
                Sei sicuro di voler procedere con l'emissione degli ordini dei contratti selezionati?
                <br><br>
                <strong>{{ contractsToIssue.length }} contratto/i</strong> verranno processati per l'emissione.
            </template>

            <template #footer>
                <button 
                    @click="showIssueConfirmation = false" 
                    class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
                >
                    Annulla
                </button>
                <button 
                    @click="confirmIssueOrdersForCopiesToBill" 
                    class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                >
                    Conferma
                </button>
            </template>
        </ConfirmationModal>

        <!-- Confirmation Modal for Update Balances -->
        <ConfirmationModal :show="showUpdateBalancesConfirmation" @close="showUpdateBalancesConfirmation = false">
            <template #title>Conferma Aggiornamento Saldi</template>

            <template #content>
                Sei sicuro di voler procedere con l'aggiornamento dei saldi dei contratti modificati?
                <br><br>
                <strong>{{ modifiedContractsCount }} contratto/i</strong> verranno aggiornati.
            </template>

            <template #footer>
                <button 
                    @click="showUpdateBalancesConfirmation = false" 
                    class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
                >
                    Annulla
                </button>
                <button 
                    @click="confirmUpdateBalances" 
                    class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                >
                    Conferma
                </button>
            </template>
        </ConfirmationModal>

        <!-- Message Modal -->
        <ModalMessage 
            v-if="showMessageModal" 
            :message="messageContent" 
            :type="messageType" 
            @close="showMessageModal = false" 
        />

        <!-- Success Message Modal -->
        <ModalMessage
            :visible="showSuccessMessage"
            :title="successTitle"
            :messages="successMessages"
            @close="showSuccessMessage = false"
        />

        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>
        <div class="p-6">
            <!-- Filtri di ricerca -->
            <div class="mb-4 bg-white rounded-md shadow-lg">
                <div @click="toggleSection('searchFilters')"
                    class="flex justify-between items-center px-6 py-3 cursor-pointer bg-gray-100 rounded-t-md">
                    <h3 class="font-semibold">Filtri di Ricerca</h3>
                    <span class="transform transition-transform" :class="{ 'rotate-180': !sections.searchFilters }">
                        &#9660;<!-- Black down arrow Unicode character -->
                    </span>
                </div>

                <div v-show="sections.searchFilters" class="p-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                        <!-- Codice contratto -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Codice contratto</label>
                            <input
                                v-model="filters.contractCode"
                                type="text"
                                class="p93__input w-full"
                                placeholder="Inserisci codice contratto"
                                @keyup.enter="searchContracts"
                            />
                        </div>

                        <!-- Cliente -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Cliente</label>
                            <v-select :options="foundClients" :filterable="false" v-model="filters.customer"
                                label="name" @search="searchClients" placeholder="Cerca cliente..." class="w-full">
                                <template #no-options="{ search, searching }">
                                    <div v-if="searching">🔍 Ricerca in corso...</div>
                                    <div v-else-if="!search">Inizia a digitare per cercare un cliente...</div>
                                </template>
                            </v-select>
                        </div>
                    </div> <!-- Bottoni cerca e reset -->
                    <div class="flex justify-end mt-4 space-x-2">
                        <button @click="resetFilters"
                            class="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
                            :disabled="searchForm.processing">
                            <i class="fa fa-undo mr-2"></i>
                            Reset
                        </button>
                        <button @click="searchContracts"
                            class="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600"
                            :disabled="searchForm.processing">
                            <i class="fa fa-search mr-2"></i>
                            {{ searchForm.processing ? 'Ricerca in corso...' : 'Cerca' }}
                        </button>
                    </div>

                    <!-- Messaggio di errore per filtri -->
                    <div v-if="searchForm.errors.filters"
                        class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                        <i class="fa fa-exclamation-triangle mr-2"></i>
                        {{ searchForm.errors.filters }}
                    </div>
                </div>
            </div>
            <!-- Risultati -->
            <div v-if="contracts.length > 0 || hasSearched" class="bg-white rounded-md shadow-lg">
                <div class="flex justify-between items-center p-4 border-b">
                    <div>
                        <h3 class="text-lg font-semibold">Risultati Ricerca</h3>
                        <div class="flex items-center space-x-3">
                            <p class="text-gray-600">{{ contracts.length }} contratto/i trovato/i</p>
                            <span v-if="hasModifiedValues" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <i class="fa fa-edit mr-1"></i>
                                {{ modifiedContractsCount }} modificato/i
                            </span>
                        </div>
                    </div>
                    <div>
                        <div class="flex items-center space-x-2">
                            <!-- Bottone Aggiorna saldi - appare quando ci sono valori modificati -->
                            <button 
                                v-if="hasModifiedValues" 
                                @click="updateBalances" 
                                class="p93__btn_primary"
                            >
                                <i class="fa fa-refresh mr-2"></i>
                                Aggiorna saldi
                            </button>

                            <button 
                                v-if="hasModifiedValues" 
                                @click="resetBalances" 
                                class="p93__btn_red"
                            >
                                Annulla
                            </button>
                            
                            <!-- Bottone Emetti ordini - appare quando ci sono copie da fatturare > 0 -->
                            <div v-if="!hasModifiedValues && hasCopiesToBill" class="flex items-center space-x-2">
                                <div class="flex items-center space-x-2">
                                    <label class="text-sm font-medium text-gray-700">Data emissione:</label>
                                    <VueDatePicker 
                                        v-model="issueDate" 
                                        locale="it" 
                                        format="dd/MM/yyyy" 
                                        auto-apply
                                        text-input
                                        :enable-time-picker="false"
                                        class="w-40"
                                        placeholder="Seleziona data"
                                        @input="pageDateInput"
                                    />
                                </div>
                                <button @click="issueOrdersForCopiesToBill" class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors duration-200">
                                    <i class="fa fa-file-export mr-2"></i>
                                    Prepara emissione ({{ contractsWithCopiesToBillCount }})
                                </button>
                            </div>

                            <!-- Assegnazione massiva data ultima rilevazione -->
                            <div v-if="!hasModifiedValues && contracts.length > 0" class="flex items-center space-x-2">
                                <div class="flex items-center space-x-2">
                                    <label class="text-sm font-medium text-gray-700">Data ultima rilevazione:</label>
                                    <VueDatePicker
                                        v-model="bulkLastDetectionDate"
                                        locale="it"
                                        format="dd/MM/yyyy"
                                        auto-apply
                                        text-input
                                        :enable-time-picker="false"
                                        class="w-40"
                                        placeholder="Seleziona data"
                                    />
                                </div>
                                <button @click="assignLastDetectionDateToAll" :disabled="!bulkLastDetectionDate" class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <i class="fa fa-calendar-check mr-2"></i>
                                    Assegna a tutte ({{ contracts.length }})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="contracts.length === 0 && hasSearched" class="p-8 text-center text-gray-500">
                    Nessun contratto trovato con i filtri specificati.
                </div>
                <div v-else>
                    <div class="overflow-y-auto" style="height: calc(90vh - 290px); min-height: 300px;">
                        <table class="min-w-full text-sm">
                            <!-- Header fisso -->
                            <thead class="bg-gray-100 sticky top-0 z-10 text-sm">
                                <tr>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Nr Contratto</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Cliente</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Articolo</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Fine validità contratto</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Data ultima rilevazione</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Data ultima rata</th>
                                    <th class="py-3 px-4 border-b text-right bg-gray-100">Valore unitario</th>
                                    <th class="py-3 px-4 border-b text-right bg-gray-100">Copie fatturate</th>
                                    <th class="py-3 px-4 border-b text-right bg-gray-100">Copie incluse</th>
                                    <th class="py-3 px-4 border-b text-right bg-gray-100">Ultimo Controllo</th>
                                    <th class="py-3 px-4 border-b text-right bg-gray-100">Copie da fatturare</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white">
                                <tr v-for="contract in contracts" :key="contract.RowId" class="hover:bg-gray-50">
                                    <td class="py-3 px-4 border-b">{{ contract.DocNo }}</td>
                                    <td class="py-3 px-4 border-b">{{ contract.CompanyName }}</td>
                                    <td class="py-3 px-4 border-b">{{ contract.Item }}</td>
                                    <td class="py-3 px-4 border-b" :class="{ 'text-red-600 font-medium': isExpiredDate(contract.EndDate) }">{{ (contract.EndDate) ? formatDate(contract.EndDate) : '-' }}</td>
                                    <td class="py-3 px-4 border-b">
                                        <VueDatePicker
                                            :model-value="parseISODate(contract.LastDetectionDate)"
                                            @update:model-value="updateLastDetectionDate(contract, $event)"
                                            locale="it"
                                            format="dd/MM/yyyy"
                                            auto-apply
                                            text-input
                                            :enable-time-picker="false"
                                            class="w-40"
                                            placeholder="-"
                                        />
                                    </td>
                                    <td class="py-3 px-4 border-b">{{ (contract.LastInstallmentDate) ? formatDate(contract.LastInstallmentDate) : '-' }}</td>
                                    <td class="py-3 px-4 border-b">{{ formatCurrency(contract.UnitValue, 3) }}</td>
                                    <td class="py-3 px-4 border-b text-right">
                                        <input 
                                            type="number" 
                                            :value="getInvoicedAmountValue(contract)"
                                            @input="updateInvoicedAmount(contract, $event.target.value)"
                                            class="w-[100px] p-1 text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            :class="{
                                                'bg-yellow-100 border-yellow-400': hasValueChanged(contract, 'invoicedAmount')
                                            }"
                                            step="1"
                                            min="0"
                                        />
                                    </td>
                                    <td class="py-3 px-4 border-b text-right">{{ contract.CopyCost }}</td>
                                    <td class="py-3 px-4 border-b text-right">
                                        <input 
                                            type="number" 
                                            :value="getLastControlValue(contract)"
                                            @input="updateLastControl(contract, $event.target.value)"
                                            class="w-[100px] p-1 text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            :class="{
                                                'bg-yellow-100 border-yellow-400': hasValueChanged(contract, 'lastControl')
                                            }"
                                            step="1"
                                            min="0"
                                        />
                                    </td>
                                    <td class="py-3 px-4 border-b text-right">
                                        <span 
                                            :class="{
                                                'bg-green-100 text-green-800 px-2 py-1 rounded': hasValueChanged(contract, 'copiesToBill'),
                                                'text-gray-900': !hasValueChanged(contract, 'copiesToBill')
                                            }"
                                        >
                                            {{ getCopiesToBillValue(contract) }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Link, router, useForm } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import ModalMessage from '@/Components/ModalMessage.vue';
import { useLoader } from '@/Composables/useLoader.js';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

export default {
    props: ['contractModels', 'initialFilters', 'orders'],
    components: {
        Link,
        BreadCrumbs,
        Loader,
        ConfirmationModal,
        ModalMessage
    },
    setup() {
        const { isLoading, loadingText, showLoader, hideLoader, withLoader, withLoaderSafe, withInertiaLoader } = useLoader();

        return {
            isLoading,
            loadingText,
            showLoader,
            hideLoader, withLoader,
            withLoaderSafe,
            withInertiaLoader
        };
    },
    data() {
        return {
            searchForm: useForm({
                contractCode: '',
                customer: null
            }),
            foundClients: [],
            contracts: [],
            selectedContracts: [],
            selectAll: false,
            hasSearched: false,
            issueDate: new Date(),
            bulkLastDetectionDate: null,
            editedValues: {},
            originalValues: {},
            sections: {
                searchFilters: true
            },
            filters: {
                contractCode: this.initialFilters?.contractCode || '',
                customer: this.initialFilters?.customer || null,
            }, navigation: [
                { name: 'Dashboard', href: route('dashboard') },
                { name: 'Contratti', href: route('modules.home', 'contratti') },
                { name: 'Controllo copie', href: route('modules.contratti.controllo-copie') }
            ],
            showIssueConfirmation: false,
            showUpdateBalancesConfirmation: false,
            showSuccessMessage: false,
            showMessageModal: false,
            contractsToIssue: [],
            successTitle: '',
            successMessages: []
        };
    },
    computed: {
        hasSelectedContracts() {
            return this.selectedContracts.length > 0;
        },
        selectableContracts() {
            return this.contracts;
        },
        hasModifiedValues() {
            return Object.keys(this.editedValues).length > 0;
        },
        modifiedContractsCount() {
            return this.getModifiedContracts().length;
        },
        hasCopiesToBill() {
            return this.contracts.some(contract => {
                const copiesToBill = this.getCopiesToBillValue(contract);
                return copiesToBill > 0;
            });
        },
        contractsWithCopiesToBillCount() {
            return this.contracts.filter(contract => {
                const copiesToBill = this.getCopiesToBillValue(contract);
                return copiesToBill > 0;
            }).length;
        }
    },
    methods: {
        toggleSection(section) {
            this.sections[section] = !this.sections[section];
        },
        isExpiredDate(dateStr) {
            if (!dateStr) return false;
            return new Date(dateStr) < new Date(new Date().toDateString());
        },
        getLastControlValue(contract) {
            return this.editedValues[contract.RowId]?.lastControl ?? contract.CopyBalance;
        },
        getInvoicedAmountValue(contract) {
            return this.editedValues[contract.RowId]?.invoicedAmount ?? (contract.InvoicedAmount || 0);
        },
        updateLastControl(contract, value) {
            if (!this.editedValues[contract.RowId]) {
                this.editedValues[contract.RowId] = {};
                this.originalValues[contract.RowId] = {
                    lastControl: contract.CopyBalance,
                    invoicedAmount: contract.InvoicedAmount || 0,
                    copiesToBill: this.getCopiesToBillValue(contract)
                };
            }
            
            const numericValue = parseFloat(value) || 0;
            this.editedValues[contract.RowId].lastControl = numericValue;
            
            // Ricalcola le copie da fatturare
            this.calculateCopiesToBill(contract);
            
            // Controlla se tutti i valori sono tornati agli originali
            this.checkAndCleanupEditedValues(contract);
        },
        updateInvoicedAmount(contract, value) {
            if (!this.editedValues[contract.RowId]) {
                this.editedValues[contract.RowId] = {};
                this.originalValues[contract.RowId] = {
                    lastControl: contract.CopyBalance,
                    invoicedAmount: contract.InvoicedAmount || 0,
                    copiesToBill: this.getCopiesToBillValue(contract)
                };
            }
            
            const numericValue = parseFloat(value) || 0;
            this.editedValues[contract.RowId].invoicedAmount = numericValue;
            
            // Ricalcola le copie da fatturare
            this.calculateCopiesToBill(contract);
            
            // Controlla se tutti i valori sono tornati agli originali
            this.checkAndCleanupEditedValues(contract);
        },
        calculateCopiesToBill(contract) {
            const lastControl = this.getLastControlValue(contract);
            const copieIncluse = contract.CopyCost || 0;
            const copieFatturate = this.getInvoicedAmountValue(contract);
            
            // Formula: ultimo controllo - copie fatturate - copie incluse
            const copieDaFatturare = lastControl - copieFatturate - copieIncluse;
            
            if (!this.editedValues[contract.RowId]) {
                this.editedValues[contract.RowId] = {};
            }
            this.editedValues[contract.RowId].copiesToBill = Math.max(0, copieDaFatturare);
        },
        checkAndCleanupEditedValues(contract) {
            if (!this.originalValues[contract.RowId] || !this.editedValues[contract.RowId]) return;
            
            const originalValues = this.originalValues[contract.RowId];
            const editedValues = this.editedValues[contract.RowId];
            
            // Controlla se tutti i valori sono tornati agli originali
            const lastControlUnchanged = editedValues.lastControl === originalValues.lastControl;
            const invoicedAmountUnchanged = editedValues.invoicedAmount === originalValues.invoicedAmount;
            
            if (lastControlUnchanged && invoicedAmountUnchanged) {
                // Se tutti i valori sono tornati agli originali, rimuovi dalla lista dei modificati
                delete this.editedValues[contract.RowId];
                delete this.originalValues[contract.RowId];
            }
        },
        getCopiesToBillValue(contract) {
            // Se esiste un valore calcolato, usalo. Altrimenti calcola al volo
            if (this.editedValues[contract.RowId]?.copiesToBill !== undefined) {
                return this.editedValues[contract.RowId].copiesToBill;
            }
            
            // Calcolo di default quando non ci sono modifiche
            const lastControl = contract.CopyBalance || 0;
            const copieIncluse = contract.CopyCost || 0;
            const copieFatturate = contract.InvoicedAmount || 0;
            const result = lastControl - copieFatturate - copieIncluse;
            return Math.max(0, result);
        },
        hasValueChanged(contract, field) {
            if (!this.originalValues[contract.RowId]) return false;
            const original = this.originalValues[contract.RowId][field];
            let current;
            switch(field) {
                case 'lastControl':
                    current = this.getLastControlValue(contract);
                    break;
                case 'invoicedAmount':
                    current = this.getInvoicedAmountValue(contract);
                    break;
                case 'copiesToBill':
                    current = this.getCopiesToBillValue(contract);
                    break;
                default:
                    current = this.getCopiesToBillValue(contract);
            }
            return original !== current;
        },
        // Metodo per ottenere i contratti con valori modificati (per future implementazioni di salvataggio)
        getModifiedContracts() {
            return Object.keys(this.editedValues).map(rowId => {
                const contract = this.contracts.find(c => c.RowId === rowId);
                if (contract) {
                    return {
                        ...contract,
                        lastControl: this.getLastControlValue(contract),
                        copiesToBill: this.getCopiesToBillValue(contract),
                        isModified: this.hasValueChanged(contract, 'lastControl') || this.hasValueChanged(contract, 'copiesToBill')
                    };
                }
                return null;
            }).filter(Boolean);
        },
        // Metodo per aggiornare i saldi (confermare tutte le modifiche)
        updateBalances() {
            // Mostra la finestra di conferma
            this.showUpdateBalancesConfirmation = true;
        },
        async confirmUpdateBalances() {
            this.showUpdateBalancesConfirmation = false;
            
            // Prepara i dati dei contratti modificati
            const modifiedContracts = this.getModifiedContracts();
            const updateForm = useForm({
                contracts: modifiedContracts.reduce((acc, contract) => {
                    acc[contract.RowId] = {
                        RowId: contract.RowId,
                        lastControl: this.getLastControlValue(contract),
                        invoicedAmount: this.getInvoicedAmountValue(contract),
                        copiesToBill: this.getCopiesToBillValue(contract)
                    };
                    return acc;
                }, {})
            });

            const result = await this.withInertiaLoader(
                updateForm,
                route('modules.contratti.aggiorna-saldi-controllo-copie'),
                {
                    preserveUrl: true,
                    onError: (error) => {
                        this.showMessageModal = true;
                        this.messageContent = error.response?.data?.message || 'Errore durante l\'aggiornamento dei saldi';
                        this.messageType = 'error';
                    },
                    onSuccess: () => {
                        
                        this.resetBalances();
                        
                        this.successTitle = 'Aggiornamento Completato';
                        this.successMessages = [
                            `${modifiedContracts.length} contratto/i sono stati aggiornati con successo.`,
                        ];
                        this.showSuccessMessage = true;
                        
                        // Aggiorna la ricerca per riflettere i nuovi dati
                        this.searchContracts();
                    },
                    onStart: () => {
                        console.log('inizio');
                        this.showLoader('Aggiornamento in corso...');
                    },
                    onFinish: () => {
                        console.log('finito');
                        this.hideLoader();
                    }
                },
                `Aggiornamento in corso per ${modifiedContracts.length} contratto/i...`
            );
        },
        resetBalances() {
            this.editedValues = {};
            this.originalValues = {};
        },
        parseISODate(dateStr) {
            if (!dateStr) return null;
            return parseISO(dateStr);
        },
        async updateLastDetectionDate(contract, date) {
            const formattedDate = date ? format(date, 'yyyy-MM-dd') : null;
            const idx = this.contracts.findIndex(c => c.RowId === contract.RowId);
            const previousDate = idx !== -1 ? this.contracts[idx].LastDetectionDate : null;
            if (idx !== -1) {
                this.contracts[idx] = { ...this.contracts[idx], LastDetectionDate: formattedDate };
            }
            try {
                await axios.post(route('modules.contratti.aggiorna-data-ultima-rilevazione'), {
                    rowIds: [contract.RowId],
                    date: formattedDate
                });
            } catch (error) {
                if (idx !== -1) {
                    this.contracts[idx] = { ...this.contracts[idx], LastDetectionDate: previousDate };
                }
                this.showMessageModal = true;
                this.messageContent = 'Errore durante l\'aggiornamento della data ultima rilevazione';
                this.messageType = 'error';
            }
        },
        async assignLastDetectionDateToAll() {
            if (!this.bulkLastDetectionDate) return;
            try {
                const formattedDate = format(this.bulkLastDetectionDate, 'yyyy-MM-dd');
                await axios.post(route('modules.contratti.aggiorna-data-ultima-rilevazione'), {
                    rowIds: this.contracts.map(c => c.RowId),
                    date: formattedDate
                });
                this.contracts = this.contracts.map(c => ({ ...c, LastDetectionDate: formattedDate }));
            } catch (error) {
                this.showMessageModal = true;
                this.messageContent = 'Errore durante l\'aggiornamento massivo della data ultima rilevazione';
                this.messageType = 'error';
            }
        },
        async issueOrdersForCopiesToBill() {
            this.contractsToIssue = this.contracts.filter(contract => {
                const copiesToBill = this.getCopiesToBillValue(contract);
                return copiesToBill > 0;
            });
            this.showIssueConfirmation = true;
        },
        async confirmIssueOrdersForCopiesToBill() {
            this.showIssueConfirmation = false;
            
            const contractsToIssue = this.contracts.filter(contract => {
                const copiesToBill = this.getCopiesToBillValue(contract);
                return copiesToBill > 0;
            });
            const issueForm = useForm({
                issueDate: format(this.issueDate, 'yyyy-MM-dd'),
                contracts: contractsToIssue.reduce((acc, contract) => {
                    acc[contract.RowId] = {
                        Customer: contract.Customer,
                        PaymentCondition: contract.PaymentCondition,
                        SalePerson: contract.SalePerson,
                        AreaManager: contract.AreaManager,
                        DocNo: contract.DocNo,
                        StartDate: contract.StartDate,
                        EndDate: contract.EndDate,
                        DueDate: contract.DueDate,
                        RowId: contract.RowId,
                        BillingPeriod: contract.BillingPeriod,
                        Amount: contract.Amount,
                        Item: contract.Item,
                        Description: contract.Description,
                        Qty: this.getCopiesToBillValue(contract), // Usa le copie da fatturare come quantità
                        UoM: contract.UoM,
                        UnitValue: contract.UnitValue,
                        CopiesToBill: this.getCopiesToBillValue(contract) // Include le copie da fatturare
                    };
                    return acc;
                }, {})
            });
            
            // Invia la richiesta
            const result = await this.withInertiaLoader(
                issueForm,
                route('modules.contratti.accoda-rate-per-emissione'),
                {
                    preserveUrl: true,
                    onError: (error) => {
                        this.showMessageModal = true;
                        this.messageContent = error.response.data.message || 'Errore durante l\'emissione degli ordini';
                        this.messageType = 'error';
                    },
                    onStart: () => {
                        this.showLoader('Emissione in corso...');
                    },
                    onFinish: () => {
                        this.hideLoader();
                    },
                    onSuccess: () => {
                        // Mostra messaggio di successo
                        this.successTitle = 'Emissione Completata';
                        this.successMessages = [
                            `${contractsToIssue.length} ordini sono stati emessi con successo.`,
                        ];
                        this.showSuccessMessage = true;
                        this.searchContracts();
                    }
                },
                `Emissione in corso per ${contractsToIssue.length} contratto/i...`
            );
        },
        searchClients(search, loading) {
            if (search.length >= 2) {
                loading(true); clearTimeout(this.searchContactsTimeout);
                this.searchContactsTimeout = setTimeout(() => {
                    axios
                        .get(route('modules.contratti.ricerca-clienti', search))
                        .then((response) => {
                            this.foundClients = response.data;
                            loading(false);
                        });
                }, 300);
            }
        },
        async searchContracts() {
            this.hasSearched = true;

            // Reset checkbox selections on new search
            this.selectedContracts = [];

            // Reset dei valori modificati
            this.resetBalances();

            this.searchForm.contractCode = this.filters.contractCode || '';
            this.searchForm.customer = this.filters.customer?.id || null;
            const result = await this.withInertiaLoader(
                this.searchForm,
                route('modules.contratti.cerca-contratti-per-controllo-copie'),
                {
                    preserveUrl: true,
                    onSuccess: (response) => {
                        this.contracts = response.props.contracts || [];
                        this.sections.searchFilters = false;
                    }
                },
                'Ricerca contratti in corso...'
            );
        },
        resetFilters() {
            this.filters = {
                contractCode: '',
                customer: null
            };

            // Reset del form Inertia
            this.searchForm.reset();
            this.searchForm.clearErrors();

            // Clear search results
            this.contracts = [];
            this.selectedContracts = [];
            this.hasSearched = false;
            this.foundClients = [];
            
            // Reset dei valori modificati
            this.resetBalances();
        },
        async issueSelectedContracts() {
            if (this.selectedContracts.length === 0) {
                alert('Seleziona almeno un contratto per l\'emissione');
                return;
            }

            this.showIssueConfirmation = true;
        }
    }
};
</script>